"use client";

import { AddToCartButton, EcommerceProductsProvider, ProductPrice, ProductSortDropdown, ProductsFilter, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SlidersHorizontal } from "lucide-react";
import { WP_Query } from "@/integrations/wordpress/wp_query";
declare const wvcClient: any;
const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
const wp_query = new WP_Query(currentWPQueryParams);
function TickEyebrow({
  label,
  className = ""
}: {
  label: string;
  className?: string;
}) {
  return <div className={`inline-flex items-center gap-3 ${className}`}>
      <span aria-hidden="true" className="block h-3 w-px bg-primary" />
      <span className="text-[0.75rem] font-semibold uppercase tracking-[0.22em] text-primary">
        {label}
      </span>
      <span aria-hidden="true" className="block h-3 w-px bg-primary" />
    </div>;
}
function FilterSidebar() {
  return <div className="space-y-6">
      <TickEyebrow label="Filtros" />
      <ProductsFilter />
    </div>;
}
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    total,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = useEcommerceProducts();
  if (loading) {
    return <div>
        <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
          <div className="h-4 w-32 animate-pulse bg-muted" />
          <div className="h-9 w-40 animate-pulse bg-muted" />
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="space-y-3">
              <div className="aspect-[4/5] w-full animate-pulse rounded-md bg-muted" />
              <div className="h-4 w-3/4 animate-pulse bg-muted" />
              <div className="h-3 w-1/2 animate-pulse bg-muted" />
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="border border-border p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {"Error al cargar servicios. "}
          <Button variant="link" onClick={refetch} className="h-auto p-0 text-primary underline">
            {"Reintentar"}
          </Button>
        </p>
      </div>;
  }
  if (products.length === 0) {
    return <div className="border border-border p-12 text-center">
        <p className="text-base text-muted-foreground">{"No hay servicios disponibles."}</p>
      </div>;
  }
  return <div>
      <div className="mb-6 flex flex-col gap-3 border-b border-border pb-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[0.8125rem] font-medium tracking-[0.01em] text-muted-foreground">
          {`Mostrando ${products.length} de ${total} servicios`}
        </p>
        <ProductSortDropdown label="Ordenar por" />
      </div>

      <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => <article key={product.id} data-index={i} className="group flex flex-col">
            <Link to={product.link} className="block">
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-md bg-accent">
                <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" loading="lazy" decoding="async" />
              </div>
            </Link>
            <div className="mt-5 space-y-2">
              <div className="flex items-start justify-between gap-4">
                <Link to={product.link} className="flex-1">
                  <h3 className="relative inline text-base font-semibold leading-tight text-foreground">
                    {product.title}
                    <span aria-hidden="true" className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
                  </h3>
                </Link>
                <ProductPrice product={product} className="shrink-0 font-mono text-[1rem] font-medium text-foreground" />
              </div>
              {product.categories && product.categories.length > 0 && <p className="text-[0.8125rem] font-medium tracking-[0.01em] text-muted-foreground">
                  {product.categories[0].name}
                </p>}
              <div className="pt-3">
                <AddToCartButton product={product} productId={product.id} variant="default" size="sm" className="w-full" buttonClassName="bg-primary text-primary-foreground w-full" text="Contratar" showQuantity={false} />
              </div>
            </div>
          </article>)}
      </div>

      {totalPages > 1 && <nav aria-label="Paginación" className="mt-12 flex items-center justify-center gap-2 border-t border-border pt-8">
          <Button variant="ghost" size="sm" onClick={prevPage} disabled={!hasPrev} className="text-[0.8125rem] font-semibold uppercase tracking-[0.04em] text-foreground hover:bg-transparent hover:text-primary disabled:opacity-40">
            {"Ant."}
          </Button>
          <div className="mx-2 flex items-center gap-1">
            {Array.from({
          length: totalPages
        }).map((_, idx) => {
          const pageNum = idx + 1;
          const isActive = pageNum === currentPage;
          return <Button key={pageNum} data-index={idx} variant="ghost" size="sm" onClick={() => setPage(pageNum)} className={`h-9 w-9 p-0 font-mono text-sm hover:bg-transparent hover:text-primary ${isActive ? "text-primary underline underline-offset-4 decoration-primary decoration-2" : "text-foreground"}`}>
                  {pageNum}
                </Button>;
        })}
          </div>
          <Button variant="ghost" size="sm" onClick={nextPage} disabled={!hasNext} className="text-[0.8125rem] font-semibold uppercase tracking-[0.04em] text-foreground hover:bg-transparent hover:text-primary disabled:opacity-40">
            {"Sig."}
          </Button>
        </nav>}
    </div>;
}
export default function Listings() {
  return <section data-section-id="24764" id="product-listings" data-nav="light" className="bg-background text-foreground py-[clamp(80px,10vw,160px)]">
      <div className="mx-auto w-full max-w-[1440px] px-[clamp(20px,4vw,72px)]">
        <EcommerceProductsProvider wp_query={wp_query}>
          {}
          <div className="mb-6 lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="h-[52px] w-full justify-between border-2 border-primary bg-transparent px-5 text-[0.8125rem] font-semibold uppercase tracking-[0.04em] text-foreground hover:bg-primary/5">
                  <span className="flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4" />
                    {"Filtros"}
                  </span>
                  <span aria-hidden="true">{"▾"}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-full overflow-y-auto bg-background sm:max-w-md">
                <SheetHeader className="border-b border-border pb-4">
                  <SheetTitle className="text-left text-lg font-semibold text-foreground">
                    {"Filtros"}
                  </SheetTitle>
                </SheetHeader>
                <div className="px-4 pb-8">
                  <FilterSidebar />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex gap-8 lg:gap-10">
            {}
            <aside className="hidden w-1/4 shrink-0 lg:block">
              <div className="sticky top-[120px]">
                <FilterSidebar />
              </div>
            </aside>

            {}
            <div className="w-full lg:w-3/4">
              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}